import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Download, Upload, Trash2, Lock, ShieldCheck, Plus, Users, Wallet, Palette, Brain, BellRing, Fingerprint } from "lucide-react";
import { notifySupported } from "../components/BillReminders";
import { bioAvailable, bioRecord } from "../lib/bio";
import { recordBackup, lastBackup } from "../components/HomeNudges";
import { useStore } from "../context/StoreContext";
import { exportBackup, importBackup } from "../lib/secureStore";
import DeviceSync from "../components/DeviceSync";
import { download, exportCSV } from "../lib/reports";
import { uid, today } from "../lib/format";
import { PageHeader, Section, NativeSelect, Money } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

function Accounts() {
  const { state, update } = useStore();
  const [n, setN] = useState({ name: "", type: "checking", balance: "" });
  return (
    <Section title="Accounts" subtitle="Balances are used for forecasts. Credit card balance = amount owed." action={<Wallet className="h-5 w-5 text-muted-foreground" />} testid="settings-accounts">
      <ul className="divide-y">
        {state.accounts.map((a) => (
          <li key={a.id} className="py-3 grid grid-cols-2 sm:grid-cols-5 gap-2 items-center" data-testid={`account-row-${a.id}`}>
            <Input defaultValue={a.name} onBlur={(e) => e.target.value !== a.name && update("Rename account", (d) => (d.accounts.find((x) => x.id === a.id).name = e.target.value))} className="sm:col-span-2" />
            <NativeSelect value={a.type} onChange={(v) => update("Change account type", (d) => (d.accounts.find((x) => x.id === a.id).type = v))}><option value="checking">Checking</option><option value="savings">Savings</option><option value="credit">Credit card</option><option value="cash">Cash</option></NativeSelect>
            <Input type="number" defaultValue={a.balance ?? ""} placeholder="Current balance" onBlur={(e) => update("Set balance", (d) => { const x = d.accounts.find((y) => y.id === a.id); x.balance = e.target.value === "" ? null : Number(e.target.value); x.balanceDate = today(); })} className="num" data-testid={`account-balance-${a.id}`} />
            <Button variant="ghost" size="sm" className="text-expense justify-self-end" onClick={() => { if (window.confirm(`Delete ${a.name} and its ${state.transactions.filter((t) => t.accountId === a.id).length} transactions?`)) update("Delete account", (d) => { d.accounts = d.accounts.filter((x) => x.id !== a.id); d.transactions = d.transactions.filter((t) => t.accountId !== a.id); }); }} data-testid={`delete-account-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
        <Input placeholder="New account name (e.g. Cash wallet)" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} className="sm:col-span-2" data-testid="new-account-name-input" />
        <NativeSelect value={n.type} onChange={(v) => setN({ ...n, type: v })} testid="new-account-type-select"><option value="checking">Checking</option><option value="savings">Savings</option><option value="credit">Credit card</option><option value="cash">Cash</option></NativeSelect>
        <Input type="number" placeholder="Balance" value={n.balance} onChange={(e) => setN({ ...n, balance: e.target.value })} data-testid="new-account-balance-input" />
        <Button onClick={() => { if (!n.name) return; update("Add account", (d) => d.accounts.push({ id: `acc-${uid()}`, name: n.name, type: n.type, balance: n.balance === "" ? null : Number(n.balance), balanceDate: today() })); setN({ name: "", type: "checking", balance: "" }); }} data-testid="add-account-button"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
    </Section>
  );
}

function Reminders() {
  const { state, update } = useStore();
  const s = state.settings;
  const set = (k, v) => update("Change setting", (d) => (d.settings[k] = v));
  const [perm, setPerm] = useState(notifySupported() ? Notification.permission : "unsupported");
  const toggle = async (v) => {
    if (v && perm !== "granted") {
      if (perm === "unsupported") return toast.error("This browser doesn't support notifications. On iPhone, add the app to your Home Screen first.");
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p !== "granted") return toast.error("Notifications are blocked. Allow them for this site in your browser settings.");
    }
    set("reminderNotify", v);
    if (v) toast.success("Bill reminders turned on");
  };
  return (
    <Section title="Bill reminders & tax" subtitle="Reminders use your confirmed bills and detected recurring payments." action={<BellRing className="h-5 w-5 text-muted-foreground" />} testid="settings-reminders">
      <div className="grid sm:grid-cols-3 gap-4">
        <div><Label>Remind me this many days before</Label><Input type="number" min="0" max="30" defaultValue={s.reminderDays ?? 3} onBlur={(e) => { const v = Math.min(30, Math.max(0, Math.round(Number(e.target.value) || 0))); e.target.value = v; set("reminderDays", v); }} data-testid="settings-reminder-days-input" /></div>
        <div>
          <Label>Browser notifications</Label>
          <div className="flex items-center gap-3 h-10"><Switch checked={!!s.reminderNotify && perm === "granted"} onCheckedChange={toggle} data-testid="settings-reminder-notify-switch" /><span className="text-xs text-muted-foreground" data-testid="settings-notify-status">{perm === "granted" ? (s.reminderNotify ? "On — shown while the app is open or installed" : "Allowed, currently off") : perm === "denied" ? "Blocked in browser settings" : perm === "unsupported" ? "Not supported in this browser" : "Off"}</span></div>
        </div>
        <div><Label>Business mileage rate ($ per mile)</Label><Input type="number" step="0.005" min="0" defaultValue={s.mileageRate ?? 0.7} onBlur={(e) => { const v = Math.max(0, Number(e.target.value) || 0); e.target.value = v; set("mileageRate", v); }} data-testid="settings-mileage-rate-input" /><p className="text-[11px] text-muted-foreground mt-1">Default is the IRS standard rate of $0.70. Update it when the IRS publishes a new rate.</p></div>
      </div>
    </Section>
  );
}

function ReceiptStores() {
  const { state, update } = useStore();
  const links = Object.entries(state.vendorLinks || {}).sort((a, b) => (b[1].lastUsed || "").localeCompare(a[1].lastUsed || ""));
  const remove = (keys) => update("Forget receipt store", (d) => { d.vendorLinks = { ...d.vendorLinks }; keys.forEach((k) => delete d.vendorLinks[k]); });
  return (
    <Section title="Receipt store matches" subtitle="Learned when you attach a scanned receipt: the store name on the receipt and the bank merchant it belongs to. These rank first next time." testid="settings-vendor-links" action={links.length > 0 && <Button variant="ghost" size="sm" onClick={() => window.confirm("Forget all learned receipt stores?") && remove(links.map(([k]) => k))} data-testid="vendor-links-clear-all">Clear all</Button>}>
      {links.length ? (
        <ul className="divide-y">
          {links.map(([k, v]) => (
            <li key={k} className="py-2 flex items-center gap-3 text-sm" data-testid={`vendor-link-${k.replace(/\s+/g, "-")}`}>
              <span className="font-medium flex-1 min-w-0 truncate">{k} <span className="text-muted-foreground font-normal">→ {v.label}</span></span>
              <span className="text-xs text-muted-foreground">{v.count}× · {v.lastUsed}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove([k])} data-testid={`vendor-link-delete-${k.replace(/\s+/g, "-")}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-muted-foreground" data-testid="vendor-links-empty">Nothing learned yet. Scan a receipt from the Tax pack and attach it to a transaction.</p>}
    </Section>
  );
}

function Members() {
  const { state, update } = useStore();
  const [name, setName] = useState("");
  return (
    <Section title="Household members" subtitle="Tag transactions to a person and filter the dashboard by member." action={<Users className="h-5 w-5 text-muted-foreground" />} testid="settings-members">
      <ul className="divide-y">
        {state.members.map((m) => (
          <li key={m.id} className="py-2 flex gap-2 items-center">
            <Input defaultValue={m.name} onBlur={(e) => e.target.value && e.target.value !== m.name && update("Rename member", (d) => (d.members.find((x) => x.id === m.id).name = e.target.value))} />
            {m.id !== "m1" && <Button variant="ghost" size="icon" onClick={() => update("Remove member", (d) => { d.members = d.members.filter((x) => x.id !== m.id); d.transactions.forEach((t) => t.memberId === m.id && (t.memberId = "m1")); })} data-testid={`delete-member-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-3">
        <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-member-input" />
        <Button onClick={() => { if (!name) return; update("Add member", (d) => d.members.push({ id: `m-${uid()}`, name })); setName(""); }} data-testid="add-member-button"><Plus className="h-4 w-4" /></Button>
      </div>
    </Section>
  );
}

// /settings?backup=1 (from the move banner) jumps to the backup section
function useScrollToBackup() {
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("backup") === "1") setTimeout(() => document.querySelector('[data-testid="settings-backup"]')?.scrollIntoView({ behavior: "smooth" }), 300);
  }, []);
}

function BiometricSetting() {
  const { turnOnBio, disableBio } = useStore();
  const [avail, setAvail] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [asking, setAsking] = useState(false);
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    bioAvailable().then(setAvail);
    bioRecord().then((r) => setEnabled(!!r));
  }, []);
  if (!avail && !enabled) return avail === false ? <p className="text-xs text-muted-foreground mt-4" data-testid="bio-unsupported">Face ID / Touch ID unlock isn't available in this browser (needs Safari 18+ or a recent Chrome with a fingerprint or face sensor).</p> : null;
  const enable = async () => {
    setBusy(true);
    try {
      await turnOnBio(pass);
      setEnabled(true);
      setAsking(false);
      setPass("");
      toast.success("Face ID / Touch ID unlock is on. Your passcode still works too.");
    } catch (e) {
      toast.error(e.message === "wrong-passcode" ? "That passcode isn't right" : e.message === "unsupported" ? "This device can't create a biometric key. Keep using your passcode." : e.name === "NotAllowedError" ? "Face ID / Touch ID was cancelled" : "Couldn't turn on Face ID / Touch ID");
    }
    setBusy(false);
  };
  return (
    <div className="mt-5 pt-5 border-t" data-testid="bio-setting">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-sm flex items-center gap-2"><Fingerprint className="h-4 w-4" /> Face ID / Touch ID unlock {enabled ? <span className="text-income text-xs" data-testid="bio-status">ON</span> : <span className="text-muted-foreground text-xs" data-testid="bio-status">OFF</span>}</p>
          <p className="text-xs text-muted-foreground">Your face or fingerprint unlocks the same encryption key as your passcode. The passcode stays as a backup.</p>
        </div>
        {enabled ? <Button variant="outline" size="sm" onClick={async () => { await disableBio(); setEnabled(false); toast.success("Face ID / Touch ID unlock turned off"); }} data-testid="bio-disable-button">Turn off</Button> : !asking && <Button size="sm" onClick={() => setAsking(true)} data-testid="bio-enable-button">Turn on</Button>}
      </div>
      {asking && !enabled && (
        <div className="flex flex-wrap gap-2 mt-3">
          <Input type="password" placeholder="Current passcode" value={pass} onChange={(e) => setPass(e.target.value)} className="max-w-xs" data-testid="bio-passcode-input" />
          <Button onClick={enable} disabled={!pass || busy} data-testid="bio-confirm-button">{busy ? "Waiting for Face ID / Touch ID…" : "Continue"}</Button>
          <Button variant="ghost" onClick={() => { setAsking(false); setPass(""); }}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

function Security() {
  const { setPasscode, hasPasscode, state, update, lockNow } = useStore();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [on, setOn] = useState(hasPasscode());
  const [ver, setVer] = useState(0);
  const save = async () => {
    if (p1.length < 4) return toast.error("Use at least 4 characters");
    if (p1 !== p2) return toast.error("Passcodes don't match");
    await setPasscode(p1);
    setOn(true);
    setVer((v) => v + 1);
    setP1("");
    setP2("");
    toast.success("Passcode set. You'll need it next time you open the app.");
  };
  return (
    <Section title="Security & privacy" action={<ShieldCheck className="h-5 w-5 text-income" />} testid="settings-security">
      <ul className="text-sm text-muted-foreground space-y-1.5 mb-5">
        <li>• All data is stored only in this browser on this device, encrypted with AES-256.</li>
        <li>• Statements are read on-device. Nothing is sent to servers or AI services. Only an anonymous page-view count leaves the device — <Link to="/settings/privacy" className="text-primary underline" data-testid="privacy-center-link">see Privacy Center</Link>.</li>
        <li>• Bank logins are never requested; PDF passwords are never stored.</li>
        <li>• Cloud sync is not included — move data between devices with an encrypted backup file.</li>
      </ul>
      <p className="font-semibold text-sm flex items-center gap-2"><Lock className="h-4 w-4" /> App passcode {on ? <span className="text-income text-xs">ON</span> : <span className="text-muted-foreground text-xs">OFF</span>}</p>
      <p className="text-xs text-muted-foreground mb-3">With a passcode, your data is encrypted with a key derived from it. If you forget it, the data can't be recovered — keep a backup.</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <Input type="password" placeholder={on ? "New passcode" : "Passcode"} value={p1} onChange={(e) => setP1(e.target.value)} data-testid="passcode-input" />
        <Input type="password" placeholder="Repeat passcode" value={p2} onChange={(e) => setP2(e.target.value)} data-testid="passcode-confirm-input" />
        <Button onClick={save} data-testid="set-passcode-button">{on ? "Change passcode" : "Set passcode"}</Button>
      </div>
      {on && <Button variant="ghost" size="sm" className="mt-2" onClick={async () => { await setPasscode(null); setOn(false); setVer((v) => v + 1); toast.success("Passcode removed"); }} data-testid="remove-passcode-button">Remove passcode</Button>}
      {on && <BiometricSetting key={ver} />}
      <div className={on ? "mt-5 pt-5 border-t grid sm:grid-cols-3 gap-4 items-end" : "mt-5 pt-5 border-t grid sm:grid-cols-3 gap-4 items-end opacity-60"} data-testid="auto-lock-settings">
        <div><Label>Auto-lock after inactivity</Label><NativeSelect value={String(state.settings.autoLockMinutes ?? 5)} onChange={(v) => update("Change setting", (d) => (d.settings.autoLockMinutes = Number(v)))} className="w-full" disabled={!on} testid="auto-lock-select">{[["0", "Off"], ["1", "1 minute"], ["5", "5 minutes"], ["15", "15 minutes"], ["30", "30 minutes"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</NativeSelect></div>
        <label className="flex items-center gap-2 text-sm h-10"><Switch disabled={!on} checked={!!state.settings.lockOnHide} onCheckedChange={(v) => update("Change setting", (d) => (d.settings.lockOnHide = v))} data-testid="lock-on-hide-switch" /> Lock when the app is hidden for over 1 minute</label>
        <Button variant="outline" disabled={!on} onClick={lockNow} data-testid="lock-now-button"><Lock className="h-4 w-4 mr-1" /> Lock now</Button>
        {!on && <p className="text-xs text-muted-foreground sm:col-span-3">Auto-lock works once a passcode is set.</p>}
      </div>
    </Section>
  );
}

function Backup() {
  const { state, restoreBackup, exportReceipts, wipe } = useStore();
  const [pw, setPw] = useState("");
  const [withReceipts, setWithReceipts] = useState(true);
  const [last, setLast] = useState(lastBackup());
  const [busy, setBusy] = useState(false);
  const allReceipts = [...state.transactions.flatMap((t) => t.receipts || []), ...(state.receiptInbox || [])];
  const receiptCount = allReceipts.length;
  const receiptBytes = allReceipts.reduce((a, r) => a + (r.size || 0), 0);
  const [restorePw, setRestorePw] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [typed, setTyped] = useState("");
  const fileRef = useRef(null);
  const doExport = async () => {
    setBusy(true);
    try {
      const receipts = withReceipts && receiptCount ? await exportReceipts() : [];
      const text = await exportBackup(state, pw, receipts);
      download(text, `household-budget-backup-${today()}${pw ? "-encrypted" : ""}.json`);
      recordBackup();
      setLast(lastBackup());
      toast.success(`${pw ? "Encrypted backup saved" : "Backup saved (not encrypted)"}${receipts.length ? ` with ${receipts.length} receipt(s)` : ""}`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't create the backup");
    }
    setBusy(false);
  };
  const doImport = async (file) => {
    try {
      const n = await restoreBackup(await importBackup(await file.text(), restorePw));
      toast.success(`Backup restored${n ? ` with ${n} receipt(s)` : ""}`);
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <Section title="Backup, restore & delete" testid="settings-backup">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="font-semibold text-sm">Create backup</p>
          <p className="text-xs text-muted-foreground mb-2">Choose a password to encrypt the file (recommended). Store it somewhere safe, e.g. iCloud Drive.</p>
          <div className="flex gap-2"><Input type="password" placeholder="Backup password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="backup-password-input" /><Button onClick={doExport} disabled={busy} data-testid="export-backup-button"><Download className="h-4 w-4 mr-1" /> {busy ? "Saving…" : "Backup"}</Button></div>
          <label className="flex items-center gap-2 text-sm mt-2"><Checkbox checked={withReceipts} onCheckedChange={(v) => setWithReceipts(!!v)} data-testid="backup-include-receipts-checkbox" /> Include receipt photos <span className="text-xs text-muted-foreground" data-testid="backup-receipts-info">({receiptCount} file{receiptCount === 1 ? "" : "s"}, about {receiptBytes < 104858 ? `${Math.max(1, Math.round(receiptBytes / 1024))} KB` : `${(receiptBytes / 1048576).toFixed(1)} MB`})</span></label>
          <p className="text-xs text-muted-foreground mt-2" data-testid="backup-last-date">{last ? `Last backup on this device: ${new Date(last).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}` : "No backup made on this device yet."}</p>
          <Button variant="link" className="px-0 mt-1" onClick={() => exportCSV(state, null)} data-testid="export-all-csv-button">Export all transactions as CSV</Button>
        </div>
        <div>
          <p className="font-semibold text-sm">Restore backup</p>
          <p className="text-xs text-muted-foreground mb-2">Replaces everything currently in the app (you can Undo).</p>
          <div className="flex gap-2"><Input type="password" placeholder="Backup password (if any)" value={restorePw} onChange={(e) => setRestorePw(e.target.value)} data-testid="restore-password-input" /><Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="restore-backup-button"><Upload className="h-4 w-4 mr-1" /> Restore</Button></div>
          <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { e.target.files[0] && doImport(e.target.files[0]); e.target.value = ""; }} data-testid="restore-file-input" />
        </div>
      </div>
      <div className="border-t mt-6 pt-5 flex flex-wrap justify-between items-center gap-3">
        <div><p className="font-semibold text-sm text-expense">Delete all data</p><p className="text-xs text-muted-foreground">Permanently erases every transaction, setting and key from this device.</p></div>
        <Button variant="destructive" onClick={() => setConfirm(true)} data-testid="delete-all-data-button"><Trash2 className="h-4 w-4 mr-1" /> Delete everything</Button>
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="delete-all-dialog">
          <DialogHeader><DialogTitle>Delete all data?</DialogTitle><DialogDescription>This cannot be undone. Type DELETE to confirm.</DialogDescription></DialogHeader>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-input" />
          <DialogFooter><Button variant="destructive" disabled={typed !== "DELETE"} onClick={async () => { await wipe(); setConfirm(false); setTyped(""); toast.success("All data deleted"); }} data-testid="delete-confirm-button">Delete permanently</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function Rules() {
  const { state, update } = useStore();
  const name = Object.fromEntries(state.categories.map((c) => [c.id, c.name]));
  return (
    <Section title="Learned categorization rules" subtitle="Created automatically when you change a category. Applied to future imports." action={<Brain className="h-5 w-5 text-muted-foreground" />} testid="settings-rules">
      {state.rules.length ? (
        <ul className="divide-y text-sm">
          {state.rules.map((r) => (
            <li key={r.key} className="py-2 flex justify-between items-center"><span>"{r.key}" → <b>{name[r.category] || r.category}</b></span><Button variant="ghost" size="icon" onClick={() => update("Delete rule", (d) => (d.rules = d.rules.filter((x) => x.key !== r.key)))}><Trash2 className="h-4 w-4" /></Button></li>
          ))}
        </ul>
      ) : <p className="text-sm text-muted-foreground">No rules yet.</p>}
    </Section>
  );
}

export default function Settings() {
  useScrollToBackup();
  const { state, update } = useStore();
  const s = state.settings;
  const set = (k, v) => update("Change setting", (d) => (d.settings[k] = v));
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Appearance, accounts, people, privacy and backups."><Button variant="outline" className="rounded-full" asChild><Link to="/settings/privacy" data-testid="open-privacy-center"><ShieldCheck className="h-4 w-4 mr-1" /> Privacy Center</Link></Button></PageHeader>
      <div className="space-y-6">
        <Section title="Preferences" action={<Palette className="h-5 w-5 text-muted-foreground" />} testid="settings-preferences">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div><Label>Appearance</Label><div className="flex items-center gap-3 h-10"><span className="text-sm">Light</span><Switch checked={s.theme === "dark"} onCheckedChange={(v) => set("theme", v ? "dark" : "light")} data-testid="settings-theme-switch" /><span className="text-sm">Dark</span></div></div>
            <div><Label>Currency</Label><NativeSelect value={s.currency} onChange={(v) => set("currency", v)} className="w-full" testid="settings-currency-select">{["USD", "CAD", "EUR", "GBP", "AUD", "NZD", "MXN", "INR", "JPY"].map((c) => <option key={c}>{c}</option>)}</NativeSelect></div>
            <div><Label>Low balance warning</Label><Input type="number" defaultValue={s.lowBalance} onBlur={(e) => set("lowBalance", Number(e.target.value) || 0)} data-testid="settings-low-balance-input" /></div>
            <div><Label>Inflation rate for comparisons (%)</Label><Input type="number" step="0.1" defaultValue={s.inflation} onBlur={(e) => set("inflation", Number(e.target.value) || 0)} data-testid="settings-inflation-input" /></div>
          </div>
        </Section>
        <Reminders />
        <ReceiptStores />
        <Accounts />
        <Members />
        <Security />
        <DeviceSync />
        <Backup />
        <Rules />
        <p className="text-xs text-muted-foreground text-center">{state.transactions.length} transactions · {state.accounts.length} accounts · {state.audit.length} history entries · Total tracked <Money value={state.transactions.reduce((a, t) => a + Math.abs(t.amount), 0)} /></p>
      </div>
    </div>
  );
}
